import { useEffect, useRef, useState } from "react";
import { Clock, X } from "lucide-react";
import { dayLabel, formatTime, instantToLocalInput, localInputToInstant } from "@/lib/format";
import { usePopover } from "@/hooks/usePopover";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

/** When the trip is planned around: now, an earliest departure, or a latest arrival. */
export type PlanTime = { when: string; arriveBy: boolean } | null;

/**
 * The chosen time as one pill under the place chips.
 *
 * It reads as the third half of the question ("from here, to there, when") and opens
 * the picker. "Nu" is the resting state, because that is what the screen is for on an
 * ordinary morning; a chosen time is spelled out with its day so "07:30" cannot be
 * mistaken for today's.
 */
export function TimePill({ time, onOpen }: { time: PlanTime; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      data-popover-anchor="time"
      className={cn(
        "pointer-events-auto inline-flex min-h-11 min-w-0 max-w-full items-center gap-1.5 rounded-full bg-[var(--color-surface)]/90 px-3.5 text-sm font-medium shadow-[var(--shadow-float)] backdrop-blur-xl",
        time && "text-[var(--color-accent)]",
      )}
    >
      <Clock className="size-4 shrink-0 text-[var(--color-muted)]" aria-hidden />
      <span className="truncate">{describe(time)}</span>
    </button>
  );
}

function describe(time: PlanTime): string {
  if (!time) return "Nu";
  const day = dayLabel(time.when);
  const clock = `${day ? `${day} ` : ""}${formatTime(time.when)}`;
  return time.arriveBy ? `Framme ${clock}` : `Avgång ${clock}`;
}

const fieldClass =
  "min-h-11 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-sm";

/** The next whole five minutes, so a fresh picker does not open on 14:37. */
function roundedNow(): string {
  const step = 5 * 60_000;
  return new Date(Math.ceil(Date.now() / step) * step).toISOString();
}

/**
 * Choosing the time, and which end of the trip it pins.
 *
 * The same native dialog as the place picker, for the same reasons. The choice is held
 * locally until "Klar": the fields fire on every keystroke, and each of those would
 * otherwise be a URL change and a round of trip requests.
 *
 * Time and day are two fields, time first. Nearly every use is "the 07:52 instead of
 * now" on the day already showing, and a combined datetime field made that a walk
 * through year, month and day before the clock could be touched. The day defaults to
 * today, so it only needs a tap when the trip is not today's.
 */
export function TimePicker({
  time,
  onPick,
  onClose,
}: {
  time: PlanTime;
  onPick: (time: PlanTime) => void;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const popover = usePopover("time", 380, onClose);
  const [initialDay, initialClock] = instantToLocalInput(time?.when ?? roundedNow()).split("T");
  const [day, setDay] = useState(initialDay ?? "");
  const [clock, setClock] = useState(initialClock ?? "");
  const [arriveBy, setArriveBy] = useState(time?.arriveBy ?? false);

  useEffect(() => {
    const node = dialog.current;
    if (node && !node.open) node.showModal();
  }, []);

  return (
    <dialog
      ref={dialog}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      aria-label="Välj tid"
      style={popover.style}
      onClick={popover.onClick}
      className={cn(
        "m-0 mt-auto w-full max-w-none rounded-t-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg)] backdrop:bg-black/50 sm:mx-auto sm:mb-auto sm:mt-[10dvh] sm:max-w-md sm:rounded-[var(--radius-card)]",
        popover.className,
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-2">
        <h2 className="text-sm font-semibold">När reser du?</h2>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Stäng">
          <X />
        </Button>
      </div>

      <form
        className="space-y-4 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          const when = localInputToInstant(`${day}T${clock}`);
          if (when) onPick({ when, arriveBy });
        }}
      >
        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Vad tiden gäller">
          <Button
            type="button"
            variant={arriveBy ? "outline" : "default"}
            onClick={() => setArriveBy(false)}
            aria-pressed={!arriveBy}
          >
            Avgång
          </Button>
          <Button
            type="button"
            variant={arriveBy ? "default" : "outline"}
            onClick={() => setArriveBy(true)}
            aria-pressed={arriveBy}
          >
            Framme senast
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="block text-sm" htmlFor="plan-time">
              {arriveBy ? "Senast framme" : "Tidigast avgång"}
            </label>
            <input
              id="plan-time"
              type="time"
              required
              value={clock}
              onChange={(e) => setClock(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm" htmlFor="plan-day">
              Dag
            </label>
            <input
              id="plan-day"
              type="date"
              required
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className={fieldClass}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={() => onPick(null)}>
            Nu
          </Button>
          <Button type="submit">Klar</Button>
        </div>
      </form>
    </dialog>
  );
}
